/**
 * A multi-vowel voice session.
 *
 * Several sustained vowels are recorded, each turned into a long-term average
 * spectrum, then combined. See spectrum.ts for why several vowels are needed
 * and why that cancels most of the microphone's own character.
 */

import { analyzeRecording, type VoiceFeatures } from './analyze';
import { TARGET_SAMPLE_RATE, resample } from './dsp';
import { combineAcross, findMissing, ltasBands, type MissingBand } from './spectrum';

export type Vowel = {
  id: string;
  /** What to show on screen. */
  say: string;
  /** A word containing the sound, so there's no ambiguity. */
  asIn: string;
};

/**
 * Five vowels spanning the corners of the vowel space.
 *
 * Chosen so their formants sit in different places: /i/ has a high second
 * formant, /u/ a low one, /a/ has both low. Between them they put energy across
 * most of the range a voice can reach, which is what makes the per-band maximum
 * meaningful.
 */
export const VOWELS: Vowel[] = [
  { id: 'ah', say: 'Ahh', asIn: 'as in father' },
  { id: 'ee', say: 'Eee', asIn: 'as in see' },
  { id: 'oo', say: 'Ooo', asIn: 'as in boot' },
  { id: 'eh', say: 'Ehh', asIn: 'as in bed' },
  { id: 'oh', say: 'Ohh', asIn: 'as in go' },
];

export type Take = {
  vowelId: string;
  spectrum: Float32Array;
  features: VoiceFeatures;
  /**
   * Mean absolute difference in dB between the first and second half of the
   * recording. Low means the spectrum had settled; high means it was still
   * moving and the reading isn't trustworthy.
   */
  stabilityDb: number;
};

/** Above this, the two halves disagree enough that the take is unreliable. */
export const STABILITY_LIMIT_DB = 4;

/**
 * Analyse one recorded vowel.
 *
 * There is no published minimum duration for a *sustained vowel* LTAS — the
 * 25-60 second figures in the literature are all for connected speech, and a
 * five-vowel battery of a few seconds each is nowhere near that. Rather than
 * guess, each take is split in half and the two halves compared: if they agree,
 * the spectrum had converged; if not, it says so.
 */
export function analyzeTake(vowelId: string, samples: Float32Array, sampleRate: number): Take {
  const signal = resample(samples, sampleRate, TARGET_SAMPLE_RATE);
  const spectrum = ltasBands(signal, TARGET_SAMPLE_RATE);

  const mid = signal.length >> 1;
  const firstHalf = ltasBands(signal.slice(0, mid), TARGET_SAMPLE_RATE);
  const secondHalf = ltasBands(signal.slice(mid), TARGET_SAMPLE_RATE);

  // Compare shapes, not levels — breath runs out and the second half is quieter.
  const centre = (s: Float32Array) => {
    let sum = 0;
    for (let i = 0; i < s.length; i++) sum += s[i];
    const mean = sum / s.length;
    return Array.from(s, (v) => v - mean);
  };
  const a = centre(firstHalf);
  const b = centre(secondHalf);
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff += Math.abs(a[i] - b[i]);

  return {
    vowelId,
    spectrum,
    features: analyzeRecording(samples, sampleRate),
    stabilityDb: Number((diff / a.length).toFixed(2)),
  };
}

export type SessionResult = {
  missing: MissingBand[];
  /** Median pitch across the takes — sets the lowest analysable band. */
  f0: number;
  takes: number;
  /** Why the result may be thin, if it is. */
  warnings: string[];
};

/** Combine every take and rank the weak regions. */
export function summarise(takes: Take[], minDepthDb = 3): SessionResult {
  const warnings: string[] = [];
  const usable = takes.filter(
    (t) => t.features.quality > 0.2 && t.features.f0 > 0 && t.stabilityDb < STABILITY_LIMIT_DB,
  );
  const unstable = takes.filter(
    (t) => t.features.quality > 0.2 && t.features.f0 > 0 && t.stabilityDb >= STABILITY_LIMIT_DB,
  );

  if (usable.length === 0) {
    return {
      missing: [],
      f0: 0,
      takes: 0,
      warnings: ['None of the recordings had enough steady sound to measure.'],
    };
  }
  if (unstable.length > 0) {
    warnings.push(
      `${unstable.length} recording${unstable.length === 1 ? '' : 's'} drifted too much between start and end to be reliable — hold a steadier note.`,
    );
  }
  const dropped = takes.length - usable.length - unstable.length;
  if (dropped > 0) {
    warnings.push(`${dropped} recording${dropped === 1 ? '' : 's'} were too quiet to measure.`);
  }
  if (usable.length < 3) {
    warnings.push('With fewer than three vowels, a normal dip can look like a gap.');
  }

  const pitches = usable.map((t) => t.features.f0).sort((a, b) => a - b);
  const f0 = pitches[pitches.length >> 1];

  const missing = findMissing(combineAcross(usable.map((t) => t.spectrum)), f0, minDepthDb);

  if (missing.length === 0) {
    warnings.push('No clear gaps found — your voice covered the range fairly evenly.');
  }

  return { missing, f0, takes: usable.length, warnings };
}
